import { CommonKVNode } from "../../../types";
import { AgencyTagFilter } from "../../../tag/AgencyTagFilter";

export type NodeFilters = {
  agencyTagFilter: AgencyTagFilter;
  keyFilter?: string;
  valueFilter?: string;
};

export const filtersActive = ({
  agencyTagFilter,
  keyFilter,
  valueFilter,
}: NodeFilters): boolean =>
  (!!keyFilter && keyFilter.length > 0) ||
  (!!valueFilter && valueFilter.length > 0) ||
  agencyTagFilter.tags.length > 0;

export const nodeMatchesFilters = (
  k: string,
  node: CommonKVNode,
  { agencyTagFilter, keyFilter, valueFilter }: NodeFilters
): boolean => {
  const matchesKeyFilter =
    keyFilter && keyFilter.length > 0
      ? k.toLowerCase().includes(keyFilter.toLowerCase())
      : true;
  const matchesValueFilter =
    valueFilter && valueFilter.length > 0
      ? (node.value
          ?.toString()
          .toLowerCase()
          .includes(valueFilter.toLowerCase()) ?? false)
      : true;
  const matchesAgencyTagFilter =
    agencyTagFilter.tags.length > 0
      ? agencyTagFilter.matchAny(node.agency_tags)
      : true;
  return matchesKeyFilter && matchesValueFilter && matchesAgencyTagFilter;
};

export enum CollapseState {
  COLLAPSED = "collapsed",
  EXPANDED = "expanded",
  MIXED = "mixed",
}

export type NodeDisplayInfo = {
  displayNode: boolean;
  // undefined = use the derived default for the current filter context.
  collapseState?: CollapseState;
};

export type NodeDisplayMap = Map<CommonKVNode, NodeDisplayInfo>;

// A node displays when it matches the filters or any descendant does.
export const buildNodeDisplayMap = (
  data: Record<string, CommonKVNode> | undefined,
  filters: NodeFilters
): NodeDisplayMap => {
  const map: NodeDisplayMap = new Map();

  const visit = (k: string, node: CommonKVNode): boolean => {
    const matchesFilters = nodeMatchesFilters(k, node, filters);
    let anyDescendantDisplays = false;
    for (const [childKey, childNode] of Object.entries(node.nodes ?? {})) {
      if (visit(childKey, childNode)) {
        anyDescendantDisplays = true;
      }
    }
    const displayNode = matchesFilters || anyDescendantDisplays;
    map.set(node, { displayNode });
    return displayNode;
  };

  for (const [key, node] of Object.entries(data ?? {})) {
    visit(key, node);
  }
  return map;
};

// How a node's subtree shows under the current filters: the collapse state
// the filters imply, the state in force once the user's toggle is applied,
// and whether the toggle is offered at all.
export type NodeCollapse = {
  matchesFilters: boolean;
  hasMatchingDescendant: boolean;
  defaultState: CollapseState;
  state: CollapseState;
  isCollapsible: boolean;
};

export const deriveNodeCollapse = (
  k: string,
  node: CommonKVNode,
  filters: NodeFilters,
  displayMap: NodeDisplayMap,
  ancestorMatches: boolean
): NodeCollapse => {
  const active = filtersActive(filters);
  const matchesFilters = nodeMatchesFilters(k, node, filters);
  const children = Object.values(node.nodes ?? {});
  const displays = (child: CommonKVNode) =>
    displayMap.get(child)?.displayNode === true;
  const hasMatchingDescendant = active && children.some(displays);

  let defaultState: CollapseState;
  if (
    !active ||
    matchesFilters ||
    ancestorMatches ||
    children.every(displays)
  ) {
    defaultState = CollapseState.EXPANDED;
  } else if (!hasMatchingDescendant) {
    defaultState = CollapseState.COLLAPSED;
  } else {
    defaultState = CollapseState.MIXED;
  }

  return {
    matchesFilters,
    hasMatchingDescendant,
    defaultState,
    state: displayMap.get(node)?.collapseState ?? defaultState,
    // A node held open to show its matching descendants stays open.
    isCollapsible: !(
      hasMatchingDescendant && defaultState === CollapseState.EXPANDED
    ),
  };
};

// The toggle's next state: a node whose default is mixed swings between
// mixed and fully expanded; any other between expanded and collapsed.
export const toggledCollapseState = ({
  defaultState,
  state,
}: NodeCollapse): CollapseState =>
  defaultState === CollapseState.MIXED
    ? state === CollapseState.EXPANDED
      ? CollapseState.MIXED
      : CollapseState.EXPANDED
    : state === CollapseState.EXPANDED
      ? CollapseState.COLLAPSED
      : CollapseState.EXPANDED;

export const collapseChevronClass = ({
  state,
  hasMatchingDescendant,
}: NodeCollapse): string => {
  if (state === CollapseState.COLLAPSED) {
    return "bi-chevron-right";
  }
  if (state === CollapseState.MIXED) {
    return "bi-chevron-double-right";
  }
  if (hasMatchingDescendant) {
    return "bi-chevron-double-down";
  }
  return "bi-chevron-down";
};
